/**
 * Runtime schemas for the shapes `catalog-api.service.ts` declares itself.
 *
 * The shapes that live in `@shared/types` have their schemas there. These are
 * the ones the admin console owns -- the admin product row, the price history,
 * the brand editor's rows and so on -- written beside the service that uses
 * them. Same rules as `catalog.schemas.ts`: loose objects, plain-string ids,
 * `.nullish()` wherever the column is nullable, closed enums only where the
 * server's own set is closed, and a `satisfies z.ZodType<Declared>` on each so
 * a declaration and its schema cannot drift apart unnoticed.
 */

import {
	type AdminListPage,
	adminListPageSchema,
	catalogBrandSchema,
	catalogCategorySchema,
	catalogProductImageSchema,
	catalogProductSchema,
	productHistoryPageSchema,
	productPriceSchema,
	specTemplateSchema,
} from "@shared/types";
import { z } from "zod";
import type {
	AdminBrandDetail,
	AdminBrandRow,
	AdminBrandSummary,
	AdminCategoryRow,
	AdminCategorySummary,
	AdminProduct,
	AdminProductDetail,
	AdminVariant,
	BrandCategoryCoverage,
	BrandNearMatch,
	BulkUpdateResult,
	CategoryMoveAssessment,
	CustomerPrice,
	MergePreview,
	PriceBasis,
	PriceHistoryEntry,
	PriceNotShownReason,
	PriceObservationStatus,
	PriceVerification,
	RecordPriceResult,
	SpecTemplateImpact,
} from "./catalog-api.service";

/** The server's `priceBasisEnum`, in the order the service declares it. */
const PRICE_BASES = [
	"ex_factory",
	"ex_works_trade",
	"list_price_ex_gst",
	"distributor_net",
	"dealer_net",
	"mrp",
	"mrp_incl_gst",
	"mrp_basis_unstated",
	"mrp_and_dealer_net",
	"recommended_consumer",
	"project_institutional",
	"landed",
	"street_indicative",
	"avg_realisation",
] as const satisfies readonly PriceBasis[];

const priceObservationStatus = [
	"published",
	"on_request",
	"not_published",
] as const satisfies readonly PriceObservationStatus[];

const priceVerification = [
	"unverified",
	"auto_validated",
	"human_reviewed",
	"brand_confirmed",
] as const satisfies readonly PriceVerification[];

const priceNotShownReason = [
	"no_amount",
	"later_price_recorded",
	"basis_not_shown",
	"no_hsn_code",
	"no_gst_rate",
	"preferred_price_has_no_gst_rate",
	"another_price_preferred",
] as const satisfies readonly PriceNotShownReason[];

// ---- products -------------------------------------------------------------

export const adminProductSchema = catalogProductSchema.extend({
	brandName: z.string().nullable(),
}) satisfies z.ZodType<AdminProduct>;

export const adminProductPageSchema = adminListPageSchema(
	adminProductSchema,
) satisfies z.ZodType<AdminListPage<AdminProduct>>;

export const adminVariantSchema = z.looseObject({
	id: z.string(),
	productId: z.string(),
	sku: z.string(),
	brandSku: z.string().nullable(),
	brandSkuKind: z.string().nullable(),
	variantAxis1Name: z.string().nullable(),
	variantAxis1Value: z.string().nullable(),
	variantAxis2Name: z.string().nullable(),
	variantAxis2Value: z.string().nullable(),
	variantAttributes: z
		.record(z.string(), z.union([z.string(), z.number()]))
		.nullable(),
	images: z.array(catalogProductImageSchema).nullable(),
	isActive: z.boolean(),
}) satisfies z.ZodType<AdminVariant>;

export const adminProductDetailSchema = z.looseObject({
	product: catalogProductSchema.extend({
		brandId: z.string().nullish(),
		verification: z.string().nullish(),
	}),
	brand: z.looseObject({ id: z.string(), name: z.string() }).nullable(),
	category: z.looseObject({
		id: z.string(),
		name: z.string(),
		slug: z.string(),
		hsnCode: z.string().nullish(),
	}),
	variants: z.array(adminVariantSchema),
	specTemplate: specTemplateSchema.nullable(),
	price: productPriceSchema.nullish(),
	prices: z.array(productPriceSchema).optional(),
}) satisfies z.ZodType<AdminProductDetail>;

export const variantEnvelopeSchema = z.looseObject({
	variant: adminVariantSchema,
});

export const productEnvelopeSchema = z.looseObject({
	product: catalogProductSchema,
});

export { productHistoryPageSchema as productHistorySchema };

export const uploadedImageSchema = z.looseObject({ url: z.string() });

export const bulkUpdateResultSchema = z.looseObject({
	updated: z.number(),
	skipped: z.array(
		z.looseObject({
			productId: z.string(),
			name: z.string(),
			reason: z.string(),
		}),
	),
}) satisfies z.ZodType<BulkUpdateResult>;

// ---- prices ---------------------------------------------------------------

export const priceHistoryEntrySchema = z.looseObject({
	id: z.string(),
	variantId: z.string().nullable(),
	basis: z.enum(PRICE_BASES),
	basisLabel: z.string(),
	amount: z.number().nullable(),
	uom: z.string(),
	city: z.string().nullable(),
	state: z.string().nullable(),
	status: z.enum(priceObservationStatus),
	effectiveFrom: z.string(),
	effectiveTo: z.string().nullable(),
	supersededById: z.string().nullable(),
	verification: z.enum(priceVerification),
	confidence: z.number().nullable(),
	source: z.string(),
	sourceUrl: z.string().nullable(),
	basisEvidence: z.string().nullable(),
	isCited: z.boolean(),
	notProjectableReason: z.string().nullable(),
}) satisfies z.ZodType<PriceHistoryEntry>;

export const priceHistorySchema = z.looseObject({
	history: z.array(priceHistoryEntrySchema),
});

const customerPriceSchema = z.looseObject({
	observationId: z.string(),
	amountPaise: z.number(),
	basis: z.enum(PRICE_BASES),
	gstRateBp: z.number(),
	isGstInclusive: z.boolean(),
}) satisfies z.ZodType<CustomerPrice>;

export const recordPriceResultSchema = z.looseObject({
	observationId: z.string(),
	supersededIds: z.array(z.string()),
	shown: z.boolean(),
	notShownReason: z.enum(priceNotShownReason).nullable(),
	customerPrice: customerPriceSchema.nullable(),
	hsnCode: z.string().nullable(),
}) satisfies z.ZodType<RecordPriceResult>;

// ---- brands ---------------------------------------------------------------

export const adminBrandSummarySchema = z.looseObject({
	id: catalogBrandSchema.shape.id,
	name: catalogBrandSchema.shape.name,
	isActive: catalogBrandSchema.shape.isActive,
	mergedIntoId: catalogBrandSchema.shape.mergedIntoId,
}) satisfies z.ZodType<AdminBrandSummary>;

export const adminBrandSummaryPageSchema = adminListPageSchema(
	adminBrandSummarySchema,
);

export const adminBrandRowSchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	isActive: z.boolean(),
	mergedIntoId: z.string().nullable(),
	productCount: z.number(),
}) satisfies z.ZodType<AdminBrandRow>;

export const adminBrandRowPageSchema = adminListPageSchema(
	adminBrandRowSchema,
) satisfies z.ZodType<AdminListPage<AdminBrandRow>>;

export const adminBrandDetailSchema = adminBrandRowSchema.extend({
	description: z.string().nullable(),
	website: z.string().nullable(),
	logoUrl: z.string().nullable(),
	contactEmail: z.string().nullable(),
	contactPhone: z.string().nullable(),
	segment: z.enum(["micro", "small", "big", "large"]).nullable(),
	positioning: z.enum(["economy", "mid", "premium", "luxury"]).nullable(),
	countryOrigin: z.string().nullable(),
	isImported: z.boolean(),
	parentBrandId: z.string().nullable(),
	manufacturer: z.string().nullable(),
	distribution: z.array(z.string()).nullable(),
	tierBasis: z.enum(["revenue", "listing", "footprint", "manual"]).nullable(),
	// A full ISO instant on the wire, not a calendar date.
	tierAsOfDate: z.string().nullable(),
}) satisfies z.ZodType<AdminBrandDetail>;

export const createdBrandSchema = z.looseObject({
	brand: z.looseObject({ id: z.string() }),
});

export const brandCategoryCoverageSchema = z.looseObject({
	categoryId: z.string(),
	slug: z.string(),
	name: z.string(),
	isPrimary: z.boolean(),
	source: z.enum(["brand-master", "manual"]),
	productCount: z.number(),
	activeCount: z.number(),
}) satisfies z.ZodType<BrandCategoryCoverage>;

export const brandNearMatchSchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	productCount: z.number(),
	reason: z.enum(["same-name", "same-house"]),
}) satisfies z.ZodType<BrandNearMatch>;

const idAndName = z.looseObject({ id: z.string(), name: z.string() });

export const mergePreviewSchema = z.looseObject({
	loser: idAndName,
	winner: idAndName,
	movedProducts: z.number().nullable(),
	collisions: z.array(z.string()),
	moreCollisions: z.boolean(),
	blocker: z.string().nullable(),
}) satisfies z.ZodType<MergePreview>;

export const mergeResultSchema = z.looseObject({ movedProducts: z.number() });

// ---- categories -----------------------------------------------------------

export const adminCategorySummarySchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	isActive: z.boolean(),
}) satisfies z.ZodType<AdminCategorySummary>;

export const adminCategoryRowSchema = catalogCategorySchema.extend({
	productCount: z.number(),
	hasTemplate: z.boolean(),
}) satisfies z.ZodType<AdminCategoryRow>;

/**
 * The categories endpoint sends `{ items, total }` and nothing else -- it is
 * deliberately unpaginated and does not go through `pageOf`.
 */
export function categoryListSchema<Item extends z.ZodType>(item: Item) {
	return z.looseObject({ items: z.array(item), total: z.number() });
}

export const createdCategorySchema = z.looseObject({
	category: z.looseObject({ id: z.string() }),
});

export const templateEnvelopeSchema = z.looseObject({
	template: specTemplateSchema.nullable(),
});

export const deletedTemplateSchema = z.looseObject({
	deletedAttributes: z.number(),
});

export const categoryMoveAssessmentSchema = z.looseObject({
	problems: z.array(
		z.looseObject({
			rule: z.enum(["self", "missing_parent", "cycle", "depth", "template"]),
			message: z.string(),
		}),
	),
	descendants: z.number(),
	resultingDepth: z.number(),
}) satisfies z.ZodType<CategoryMoveAssessment>;

export const reorderResultSchema = z.looseObject({ updated: z.number() });

export const specTemplateImpactSchema = z.looseObject({
	productCount: z.number(),
	orphaned: z.array(z.looseObject({ name: z.string(), products: z.number() })),
	newlyRequired: z.array(
		z.looseObject({
			name: z.string(),
			missing: z.number(),
			active: z.number(),
		}),
	),
	retyped: z.array(
		z.looseObject({
			name: z.string(),
			from: z.string(),
			to: z.string(),
			stored: z.number(),
		}),
	),
	narrowedEnums: z.array(
		z.looseObject({
			name: z.string(),
			removed: z.array(z.string()),
			products: z.number(),
		}),
	),
	droppedAxes: z
		.array(z.looseObject({ name: z.string(), variants: z.number() }))
		.optional(),
}) satisfies z.ZodType<SpecTemplateImpact>;

export const adminCategorySummaryListSchema = categoryListSchema(
	adminCategorySummarySchema,
);

export const adminCategoryRowListSchema = categoryListSchema(
	adminCategoryRowSchema,
);

export const brandCategoryCoverageListSchema = z.array(
	brandCategoryCoverageSchema,
);

export const brandNearMatchListSchema = z.array(brandNearMatchSchema);
